import React, { useMemo } from "react";

function toDate(value) {
  if (!value) return null;

  try {
    const parsed =
      typeof value?.toDate === "function"
        ? value.toDate()
        : new Date(value);

    return Number.isNaN(parsed.getTime())
      ? null
      : parsed;
  } catch {
    return null;
  }
}

function formatDateTime(value) {
  const parsed = toDate(value);

  return parsed
    ? parsed.toLocaleString()
    : "Date unavailable";
}

function toNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function getLatitude(record) {
  return toNumber(
    record?.latitude ??
      record?.lat ??
      record?.coords?.latitude ??
      record?.location?.latitude
  );
}

function getLongitude(record) {
  return toNumber(
    record?.longitude ??
      record?.lng ??
      record?.lon ??
      record?.coords?.longitude ??
      record?.location?.longitude
  );
}

function getTimestamp(record) {
  return (
    record?.timestamp ||
    record?.recordedAt ||
    record?.createdAt ||
    record?.updatedAt ||
    record?.arrivedAt
  );
}

function titleCase(value) {
  return String(value || "location")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) =>
      character.toUpperCase()
    );
}

function calculateDistanceMeters(
  lat1,
  lng1,
  lat2,
  lng2
) {
  if (
    [lat1, lng1, lat2, lng2].some(
      (value) => !Number.isFinite(value)
    )
  ) {
    return null;
  }

  const earthRadius = 6371000;
  const radians = (degrees) =>
    (degrees * Math.PI) / 180;

  const deltaLatitude = radians(lat2 - lat1);
  const deltaLongitude = radians(lng2 - lng1);

  const a =
    Math.sin(deltaLatitude / 2) ** 2 +
    Math.cos(radians(lat1)) *
      Math.cos(radians(lat2)) *
      Math.sin(deltaLongitude / 2) ** 2;

  const c =
    2 *
    Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return earthRadius * c;
}

function formatDistance(value) {
  if (!Number.isFinite(value)) return "Unavailable";

  if (value < 1000) {
    return `${Math.round(value)} m`;
  }

  return `${(value / 1609.344).toFixed(2)} mi`;
}

export default function Shift360Location({
  shift,
  facility,
}) {
  const locationData = useMemo(() => {
    if (!shift) {
      return {
        points: [],
        facilityLat: null,
        facilityLng: null,
        radius: 200,
      };
    }

    const facilityLat = toNumber(
      facility?.facilityLat ??
        facility?.latitude ??
        shift?.facilityLat
    );

    const facilityLng = toNumber(
      facility?.facilityLng ??
        facility?.longitude ??
        shift?.facilityLng
    );

    const radius =
      toNumber(
        facility?.geoRadiusMeters ??
          shift?.geoRadiusMeters
      ) || 200;

    const combined = [
      ...(Array.isArray(shift?.gpsHistory)
        ? shift.gpsHistory
        : []),
      ...(Array.isArray(shift?.locationHistory)
        ? shift.locationHistory
        : []),
      ...(Array.isArray(shift?.trackingHistory)
        ? shift.trackingHistory
        : []),
    ];

    const arrivalLat = toNumber(
      shift?.arrivalLat ??
        shift?.arrivedLat ??
        shift?.arrivalLocation?.latitude
    );

    const arrivalLng = toNumber(
      shift?.arrivalLng ??
        shift?.arrivedLng ??
        shift?.arrivalLocation?.longitude
    );

    if (
      Number.isFinite(arrivalLat) &&
      Number.isFinite(arrivalLng)
    ) {
      combined.push({
        id: "recorded-arrival",
        latitude: arrivalLat,
        longitude: arrivalLng,
        timestamp:
          shift?.arrivedAt ||
          shift?.checkedInAt ||
          shift?.arrivalTime,
        type: "arrival",
        accuracy:
          shift?.arrivalAccuracy ||
          shift?.gpsAccuracy,
      });
    }

    const points = combined
      .map((record, index) => {
        if (!record || typeof record !== "object") {
          return null;
        }

        const latitude = getLatitude(record);
        const longitude = getLongitude(record);

        if (
          !Number.isFinite(latitude) ||
          !Number.isFinite(longitude)
        ) {
          return null;
        }

        const distance = calculateDistanceMeters(
          facilityLat,
          facilityLng,
          latitude,
          longitude
        );

        return {
          id:
            record?.id ||
            `location-${index}`,
          latitude,
          longitude,
          timestamp: getTimestamp(record),
          type:
            record?.type ||
            record?.event ||
            record?.status ||
            "location",
          accuracy: toNumber(
            record?.accuracy ??
              record?.coords?.accuracy
          ),
          distance,
          insideRadius:
            Number.isFinite(distance)
              ? distance <= radius
              : null,
        };
      })
      .filter(Boolean)
      .sort((a, b) => {
        const aDate = toDate(a.timestamp);
        const bDate = toDate(b.timestamp);

        return (
          (bDate?.getTime() || 0) -
          (aDate?.getTime() || 0)
        );
      });

    return {
      points,
      facilityLat,
      facilityLng,
      radius,
    };
  }, [shift, facility]);

  if (!shift) return null;

  const arrivalPoint =
    locationData.points.find(
      (point) =>
        String(point.type).toLowerCase() ===
        "arrival"
    ) || locationData.points[0];

  return (
    <div style={{ display: "grid", gap: 13 }}>
      <div>
        <div
          style={{
            color: "#0f172a",
            fontSize: 18,
            fontWeight: 950,
          }}
        >
          GPS & Arrival History
        </div>

        <div
          style={{
            marginTop: 4,
            color: "#64748b",
            fontSize: 11,
            fontWeight: 700,
          }}
        >
          Recorded worker locations and arrival
          verification for this shift.
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            "repeat(2, minmax(0, 1fr))",
          gap: 8,
        }}
      >
        <div
          style={{
            padding: 12,
            borderRadius: 13,
            border: "1px solid #dbeafe",
            background: "#eff6ff",
          }}
        >
          <div
            style={{
              color: "#64748b",
              fontSize: 9,
              fontWeight: 900,
              textTransform: "uppercase",
            }}
          >
            Location Records
          </div>

          <div
            style={{
              marginTop: 5,
              color: "#1d4ed8",
              fontSize: 19,
              fontWeight: 950,
            }}
          >
            {locationData.points.length}
          </div>
        </div>

        <div
          style={{
            padding: 12,
            borderRadius: 13,
            border: "1px solid #e2e8f0",
            background: "#f8fafc",
          }}
        >
          <div
            style={{
              color: "#64748b",
              fontSize: 9,
              fontWeight: 900,
              textTransform: "uppercase",
            }}
          >
            Arrival Radius
          </div>

          <div
            style={{
              marginTop: 5,
              color: "#0f172a",
              fontSize: 19,
              fontWeight: 950,
            }}
          >
            {locationData.radius} m
          </div>
        </div>
      </div>

      <div
        style={{
          padding: 13,
          borderRadius: 14,
          border:
            arrivalPoint?.insideRadius === true
              ? "1px solid #bbf7d0"
              : arrivalPoint?.insideRadius === false
              ? "1px solid #fecaca"
              : "1px solid #e2e8f0",
          background:
            arrivalPoint?.insideRadius === true
              ? "#f0fdf4"
              : arrivalPoint?.insideRadius === false
              ? "#fef2f2"
              : "#f8fafc",
        }}
      >
        <div
          style={{
            color:
              arrivalPoint?.insideRadius === true
                ? "#166534"
                : arrivalPoint?.insideRadius === false
                ? "#b91c1c"
                : "#475569",
            fontSize: 13,
            fontWeight: 950,
          }}
        >
          Arrival Verification
        </div>

        <div
          style={{
            marginTop: 6,
            color: "#475569",
            fontSize: 10,
            fontWeight: 750,
            lineHeight: 1.45,
          }}
        >
          {!arrivalPoint
            ? "No arrival coordinates were recorded."
            : arrivalPoint.insideRadius === true
            ? `Worker location was within the ${locationData.radius}-meter facility radius.`
            : arrivalPoint.insideRadius === false
            ? `Worker location was outside the ${locationData.radius}-meter facility radius.`
            : "Arrival coordinates exist, but the facility coordinates are unavailable for distance verification."}
        </div>

        {arrivalPoint ? (
          <div
            style={{
              marginTop: 7,
              color: "#64748b",
              fontSize: 9,
              fontWeight: 750,
            }}
          >
            Distance from facility:{" "}
            {formatDistance(arrivalPoint.distance)}
          </div>
        ) : null}
      </div>

      {locationData.points.length === 0 ? (
        <div
          style={{
            padding: 18,
            borderRadius: 13,
            border: "1px dashed #cbd5e1",
            background: "#f8fafc",
            color: "#64748b",
            textAlign: "center",
            fontSize: 11,
            fontWeight: 750,
          }}
        >
          No GPS or location records are available for
          this shift.
        </div>
      ) : (
        <div style={{ display: "grid", gap: 9 }}>
          {locationData.points.map((point) => (
            <div
              key={point.id}
              style={{
                padding: 12,
                borderRadius: 13,
                border: "1px solid #e2e8f0",
                background: "#ffffff",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: 9,
                }}
              >
                <div>
                  <div
                    style={{
                      color: "#0f172a",
                      fontSize: 11,
                      fontWeight: 950,
                    }}
                  >
                    {titleCase(point.type)}
                  </div>

                  <div
                    style={{
                      marginTop: 4,
                      color: "#64748b",
                      fontSize: 9,
                      fontWeight: 750,
                    }}
                  >
                    {formatDateTime(point.timestamp)}
                  </div>
                </div>

                {point.insideRadius !== null ? (
                  <span
                    style={{
                      padding: "4px 7px",
                      borderRadius: 999,
                      color: point.insideRadius
                        ? "#166534"
                        : "#b91c1c",
                      background: point.insideRadius
                        ? "#dcfce7"
                        : "#fee2e2",
                      fontSize: 8,
                      fontWeight: 950,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {point.insideRadius
                      ? "IN RANGE"
                      : "OUTSIDE RANGE"}
                  </span>
                ) : null}
              </div>

              <div
                style={{
                  display: "grid",
                  gap: 4,
                  marginTop: 9,
                  color: "#475569",
                  fontSize: 9,
                  fontWeight: 750,
                }}
              >
                <div>
                  Latitude: {point.latitude}
                </div>

                <div>
                  Longitude: {point.longitude}
                </div>

                <div>
                  Distance:{" "}
                  {formatDistance(point.distance)}
                </div>

                <div>
                  Accuracy:{" "}
                  {Number.isFinite(point.accuracy)
                    ? `${Math.round(
                        point.accuracy
                      )} m`
                    : "Unavailable"}
                </div>
              </div>

              <a
                href={`https://www.google.com/maps?q=${point.latitude},${point.longitude}`}
                target="_blank"
                rel="noreferrer"
                style={{
                  display: "inline-flex",
                  marginTop: 9,
                  padding: "6px 9px",
                  borderRadius: 8,
                  background: "#eff6ff",
                  color: "#1d4ed8",
                  textDecoration: "none",
                  fontSize: 9,
                  fontWeight: 900,
                }}
              >
                Open Location
              </a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}